import { useEffect } from 'react';
import { toast, type NotificationAddData } from '@doxyme/ui/components';
import { ButtonType } from '@doxyme/ui-foundation/components/button';
import { type Message } from '@doxyme/extensions-common';
import { NotificationCapability, type Specs } from '@doxyme/extensions-bridge';
import { useAddCapability } from '@doxyme/extensions-bridge-react';
import { bridge } from '../../../features/extensions/instances';
import { useAppDispatch } from '../../useReduxToolkit';
import {
  addNotification,
  dismissNotification,
} from '../../../features/notifications/notifications.slice';
import { getNotificationIcon } from './getNotificationIcon';

type NotificationSnackbarPayload =
  Specs.Notification.NotificationSnackbarPayload;
type NotificationToastPayload = Specs.Notification.NotificationToastPayload;
type DismissPayload = Specs.Notification.DismissPayload;

const getCallbackFn = (
  sendMessage: (message: Message) => void,
  message?: NotificationSnackbarPayload['callback'],
): NotificationAddData['callback'] => {
  return message !== undefined
    ? () => {
        sendMessage(message);
      }
    : undefined;
};

export function useExtensionsNotification(): void {
  const dispatch = useAppDispatch();
  const notification = useAddCapability(
    bridge,
    () => new NotificationCapability(),
  );

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];
    unsubscribes.push(
      notification.onSnackbar(
        (payload: NotificationSnackbarPayload, sendMessage): void =>
          void dispatch(
            addNotification({
              ...payload,
              type: 'extensionSnackbar',
              icon: getNotificationIcon(payload), // Map to JSX element
              buttonType: payload.buttonType as ButtonType,
              callback: getCallbackFn(sendMessage, payload.callback),
            }),
          ),
      ),
      notification.onToast((payload: NotificationToastPayload): void => {
        toast({
          title: payload.title,
          icon: getNotificationIcon(payload)!,
        });
      }),
      notification.onDismiss(
        (payload: DismissPayload): void =>
          void dispatch(dismissNotification(payload)),
      ),
    );

    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
    };
  }, []);
}
